import { Column, Row } from 'app/design/primitives/box';
import { SearchInput } from 'app/design/primitives/input/search';
import { Text } from 'app/design/primitives/text';

import { ArrowTopRight } from 'app/design/icons/arrow-top-right';
import { useAllTags } from 'app/network/listing';
import { useUser } from 'app/network/user';
import { useClickOutside } from 'app/hooks/use-click-outside';
import { isMobileNative } from 'app/utils/web-helpers';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import type { TextInput } from 'react-native';
import { useClickOutside as useRnClickOutside } from 'react-native-click-outside';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconArrowLeft, IconClock, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

type SearchOverlayVariant = 'fullscreen' | 'dropdown';

export function SearchOverlay({
  onClose,
  onSubmit,
  onTagPress,
  onViewAllListings,
  variant = 'fullscreen',
  withSearchInput = true,
  initialValue = '',
}: {
  onClose: () => void;
  onSubmit: (query: string) => void;
  onTagPress: (tagId: string) => void;
  onViewAllListings: () => void;
  variant?: SearchOverlayVariant;
  withSearchInput?: boolean;
  initialValue?: string;
}) {
  const { data: tags } = useAllTags(true);
  const inputRef = React.useRef<TextInput>(null);
  const [searchValue, setSearchValue] = useState(initialValue);
  const { data: user } = useUser();
  const { t } = useTranslation('landing-page');
  const insets = useSafeAreaInsets();
  const menuRef = React.useRef(null);
  useClickOutside([menuRef], (clickedOutside) => {
    if (clickedOutside) {
      onClose();
    }
  });

  const asRef = useRnClickOutside(() => {
    onClose();
  });

  const ref = isMobileNative ? asRef : menuRef;

  React.useEffect(() => {
    if (!withSearchInput) return;
    const timeout = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timeout);
  }, [withSearchInput]);

  const handleRecentSearchPress = (query: string) => {
    setSearchValue(query);
    onSubmit(query);
  };

  const overlayContent = (
    <View
      ref={ref}
      style={[
        variant === 'dropdown' ? styles.dropdownWrapper : styles.fullscreenWrapper,
        variant === 'fullscreen' ? { paddingTop: insets.top + theme.spacing['spacing.general.x2'] } : undefined,
      ]}
    >
      <ScrollView
        style={variant === 'dropdown' ? styles.dropdownContainer : styles.overlayContainer}
        contentContainerStyle={styles.contentContainer}
        keyboardShouldPersistTaps="handled"
      >
        {withSearchInput ? (
          <Row alignItems="center" gap="spacing.general.x2" marginBottom="spacing.general.x5">
            <Pressable
              accessibilityLabel={t('close', { ns: 'accessibility' })}
              accessibilityRole="button"
              hitSlop={10}
              onPress={() => onClose()}
            >
              <IconArrowLeft color={theme.colors['text.primary']} strokeWidth={ICON_STROKE_WIDTH} />
            </Pressable>
            <Column flex={1}>
              <SearchInput
                innerRef={inputRef}
                initialValue={searchValue}
                submitOnEmptyChange={false}
                onSubmit={(query) => {
                  const normalizedQuery = query.trim();
                  setSearchValue(query);
                  onSubmit(normalizedQuery);
                }}
                right={
                  searchValue.length > 0 ? (
                    <Pressable
                      accessibilityHint={t('search.clearSearchHint', { ns: 'accessibility' })}
                      accessibilityLabel={t('search.clearSearch', { ns: 'accessibility' })}
                      accessibilityRole="button"
                      hitSlop={10}
                      onPress={() => setSearchValue('')}
                    >
                      <IconX strokeWidth={ICON_STROKE_WIDTH} size={20} />
                    </Pressable>
                  ) : undefined
                }
              />
            </Column>
          </Row>
        ) : null}

        {(user?.recentSearches?.length ?? 0) > 0 && (
          <Column gap="spacing.general.x2" marginBottom="spacing.general.x5">
            <Text fontSize={15} fontWeight="700">
              {t('landing-page:recentSearches')}
            </Text>
            {user?.recentSearches?.map((query) => (
              <Pressable key={query} onPress={() => handleRecentSearchPress(query)} style={styles.recentSearchRow}>
                <Row alignItems="center" gap="spacing.general.x2" flex={1}>
                  <IconClock strokeWidth={ICON_STROKE_WIDTH} size={16} color={theme.colors['text.secondary']} />
                  <Text fontSize={14} flex={1}>
                    {query}
                  </Text>
                </Row>
                <ArrowTopRight
                  height={16}
                  width={16}
                  color={theme.colors['text.secondary']}
                  style={{ transform: [{ rotate: '-90deg' }] }}
                />
              </Pressable>
            ))}
          </Column>
        )}

        <Column gap="spacing.general.x2" testID="popular-interests-overlay">
          <Row justifyContent="space-between" alignItems="center">
            <Text fontSize={15} fontWeight="700">
              {t('landing-page:popularTags')}
            </Text>
            <Pressable testID="search-overlay-view-all-listings-button" onPress={onViewAllListings}>
              <Text color="text.interactive" fontSize={14} fontWeight="600">
                {t('landing-page:viewAllListings')}
              </Text>
            </Pressable>
          </Row>
          {tags && tags.length > 0 ? (
            <Row style={{ flexWrap: 'wrap', gap: theme.spacing['spacing.general.x1'] }}>
              {tags.slice(0, 10).map((tag) => (
                <Pressable key={tag.id} onPress={() => onTagPress(tag.id)} style={styles.tagPill}>
                  <Text fontSize={13} fontWeight="500">
                    {tag.tagName}
                  </Text>
                </Pressable>
              ))}
            </Row>
          ) : null}
        </Column>
      </ScrollView>
    </View>
  );

  if (variant === 'fullscreen') {
    return (
      <Modal animationType="none" onRequestClose={onClose} presentationStyle="fullScreen" visible>
        {overlayContent}
      </Modal>
    );
  }

  return overlayContent;
}

const styles = StyleSheet.create({
  fullscreenWrapper: {
    backgroundColor: theme.colors['surface.default'],
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1200,
  },
  dropdownWrapper: {
    width: '100%',
  },
  overlayContainer: {
    backgroundColor: theme.colors['surface.default'],
    flex: 1,
  },
  dropdownContainer: {
    width: '100%',
    maxHeight: 520,
    backgroundColor: theme.colors['surface.default'],
    borderRadius: theme.borderRadii['radius.xl'],
    borderWidth: 1,
    borderColor: theme.colors['surface.tertiary-neutral'],
    boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.12)',
  },
  contentContainer: {
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingTop: theme.spacing['spacing.general.x4'],
    paddingBottom: theme.spacing['spacing.general.x12'],
  },
  recentSearchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing['spacing.general.x1'],
  },
  tagPill: {
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    borderRadius: theme.borderRadii['radius.full'],
    paddingHorizontal: theme.spacing['spacing.general.x3'],
    paddingVertical: theme.spacing['spacing.general.x1'],
  },
});
